import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Button, InputNumber, Select } from "antd";
import { DeleteOutlined, PictureOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import parseMediaUrl from "../../../../functions/parseMediaUrl";
import { getApiSetup } from "../../../../../../services/backend/api.service";

/**
 * The standings table typed in by hand, for competitions Opta has no standings
 * feed for.
 *
 * Teams are picked from the same per-competition registry the matchdays use, so
 * a badge uploaded once when creating a matchday is reused here -- the row only
 * needs its position, points and goal difference.
 *
 * Rows are stored under `manualEntries.standings`, which rides along in
 * `data.editData` exactly like the manual scorers do, so nothing new has to be
 * added to the template's `fields` or to the save pipeline.
 */

// The table prints every team ranked 10 or better, so ten rows is the useful
// default. Rows beyond that are allowed -- a tie at 10th legitimately shows
// more -- they simply will not be printed.
const DEFAULT_ROWS = 10;

const buildRow = (order) => ({
  rowId: `row_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`,
  order,
  teamId: null,
  name: "",
  logoUrl: "",
  points: null,
  goaldifference: null,
});

const ContentTableManual = ({ templateData, manualEntries, setManualEntries }) => {
  const { t } = useTranslation();
  const competitionId = _.get(templateData, "event.competitionId");

  const [teams, setTeams] = useState([]);

  const rows = useMemo(() => {
    const stored = _.get(manualEntries, "standings");
    if (_.isArray(stored) && stored.length) return stored;
    return Array.from({ length: DEFAULT_ROWS }, (_unused, i) => buildRow(i + 1));
  }, [manualEntries]);

  useEffect(() => {
    let cancelled = false;
    if (!competitionId) return undefined;

    getApiSetup(1)
      .then((data) => {
        if (cancelled) return;
        const stored = _.get(
          data,
          ["data", "data", "manualTeams", competitionId],
          [],
        );
        setTeams(_.isArray(stored) ? stored : []);
      })
      .catch((err) => console.error("err", err));

    return () => {
      cancelled = true;
    };
  }, [competitionId]);

  // Always writes the whole array back. `manualEntries` is one object shared
  // with the other manual editors, so the spread preserves anything they own.
  const commit = (next) => {
    setManualEntries({ ...(manualEntries || {}), standings: next });
  };

  const updateRow = (rowId, patch) => {
    commit(rows.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
  };

  const onTeamChange = (rowId, teamId) => {
    const team = teams.find((item) => item.id === teamId);
    // Name and badge are copied onto the row rather than looked up at render
    // time, so the saved template keeps the table exactly as it was published
    // even if the team is renamed later.
    updateRow(rowId, {
      teamId: team?.id || null,
      name: team?.name || "",
      logoUrl: team?.logoUrl || "",
    });
  };

  const onAddRow = () => {
    const nextOrder =
      rows.reduce((max, row) => Math.max(max, Number(row.order) || 0), 0) + 1;
    commit([...rows, buildRow(nextOrder)]);
  };

  const onDeleteRow = (rowId) => {
    commit(rows.filter((row) => row.rowId !== rowId));
  };

  if (!competitionId) {
    return (
      <p className={"empty-text"}>{t("Select a matchday to begin editing.")}</p>
    );
  }

  return (
    <div className={"content-tab manual-standings"}>
      <div className={"manual-standings-head"}>
        <span className={"col-order"}>{t("Pos")}</span>
        <span className={"col-team"}>{t("Team")}</span>
        <span className={"col-num"}>{t("PTS")}</span>
        <span className={"col-num"}>{t("GD")}</span>
        <span className={"col-actions"} />
      </div>

      {/* Sorted for display only -- `order` is what the template sorts by too,
          so what is shown here is the printed order. */}
      {_.sortBy(rows, (row) => Number(row.order) || 0).map((row) => {
        const logo = parseMediaUrl(row.logoUrl || "");
        return (
          <div className={"manual-standings-row"} key={row.rowId}>
            <InputNumber
              className={"col-order"}
              min={1}
              value={row.order}
              onChange={(value) => updateRow(row.rowId, { order: value })}
            />

            <span className={"col-team"}>
              {logo ? (
                <img className={"manual-standings-logo"} src={logo} alt="" />
              ) : (
                <span className={"manual-standings-logo manual-standings-logo-empty"}>
                  <PictureOutlined />
                </span>
              )}
              <Select
                className={"manual-standings-select"}
                showSearch
                allowClear
                placeholder={t("Select a team")}
                value={row.teamId || undefined}
                optionFilterProp={"title"}
                onChange={(teamId) => onTeamChange(row.rowId, teamId)}
                options={teams.map((team) => ({
                  value: team.id,
                  title: team.name,
                  label: team.name,
                }))}
              />
            </span>

            <InputNumber
              className={"col-num"}
              value={row.points}
              onChange={(value) => updateRow(row.rowId, { points: value })}
            />

            <InputNumber
              className={"col-num"}
              value={row.goaldifference}
              onChange={(value) =>
                updateRow(row.rowId, { goaldifference: value })
              }
            />

            <DeleteOutlined
              className={"col-actions manual-standings-delete"}
              title={t("Remove row")}
              onClick={() => onDeleteRow(row.rowId)}
            />
          </div>
        );
      })}

      <Button
        className={"manual-standings-add"}
        icon={<PlusOutlined />}
        onClick={onAddRow}
        block
      >
        {t("Add row")}
      </Button>

      {teams.length === 0 && (
        <p className={"empty-text"}>
          {t("No teams yet - add them when creating a matchday.")}
        </p>
      )}
    </div>
  );
};

export default ContentTableManual;
